import React, { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { ChatMessage as ChatMessageType, ChatAttachment, chatService } from '../../services/chatService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Reply, FileText, Image, File, Download, CheckSquare, MoreVertical, Maximize2, X, Play, AlertCircle, ZoomIn, ZoomOut, RotateCcw, Mic, Pencil, Trash2, Check } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { SIGNED_URL_TTL_SECONDS, cacheAindaValido } from './attachmentUrl';
import { tipoDoAnexo } from './attachmentKind';
import MessageToTaskModal from './MessageToTaskModal';
import MessageContextMenu from './MessageContextMenu';
import { useOrganization } from '../../contexts/OrganizationContext';
import { simpleTaskService } from '../../services/simpleTaskService';
import { useChat } from '../../contexts/ChatContext';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../contexts/AuthContext';
import { splitMessageIntoTokens } from './messageTokens';
import { ReactionPills, ReactionButton } from './MessageReactions';
import { ChatMessageReaction } from './reactions';
import {
  TEXTO_DE_MENSAGEM_APAGADA,
  foiApagada,
  foiEditada,
  podeEditar,
  podeExcluir,
  edicaoVaiAoBanco,
} from './messageEditing';
import './ChatMessage.css';

// Estendendo a interface ChatMessageType para incluir as propriedades que estamos usando
interface ExtendedChatMessage extends ChatMessageType {
  reply_to?: string;
  reply_to_message?: {
    id: string;
    content: string;
    // A citação precisa saber que a mensagem original foi apagada, ou continua
    // exibindo o texto que a pessoa mandou remover.
    deleted_at?: string | null;
    user?: {
      id?: number;
      name?: string;
      avatar_url?: string;
    }
  }
}

interface ChatMessageProps {
  message: ExtendedChatMessage;
  isOwnMessage: boolean;
  onReply?: () => void;
  // Clique na citação leva até a mensagem original.
  onJumpToMessage?: (messageId: string) => void;
  // Destaque breve depois de chegar aqui por uma citação.
  highlighted?: boolean;
}

// Hora de envio, não "há X tempo": a pergunta que o rodapé da mensagem precisa
// responder é "que horas isso foi enviado". Mensagem de hoje mostra só o
// relógio; de outro dia, a data junto. `hoje` é parâmetro pra poder testar.
export function formatMessageTime(createdAt?: string, hoje: Date = new Date()): string {
  if (!createdAt) return '';

  const enviadaEm = new Date(createdAt);
  if (Number.isNaN(enviadaEm.getTime())) return '';

  const mesmoDia =
    enviadaEm.getFullYear() === hoje.getFullYear() &&
    enviadaEm.getMonth() === hoje.getMonth() &&
    enviadaEm.getDate() === hoje.getDate();

  if (mesmoDia) return format(enviadaEm, 'HH:mm', { locale: ptBR });
  if (enviadaEm.getFullYear() === hoje.getFullYear()) {
    return format(enviadaEm, "dd/MM 'às' HH:mm", { locale: ptBR });
  }
  return format(enviadaEm, "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
}

// Data por extenso com segundos, para o tooltip.
export function formatMessageTimeFull(createdAt?: string): string {
  if (!createdAt) return '';

  const enviadaEm = new Date(createdAt);
  if (Number.isNaN(enviadaEm.getTime())) return '';

  return format(enviadaEm, "EEEE, dd 'de' MMMM 'de' yyyy 'às' HH:mm:ss", { locale: ptBR });
}

const getIconForFileType = (fileType: string) => {
  const tipo = tipoDoAnexo(fileType);

  if (tipo === 'imagem') {
    return <Image className="w-4 h-4 mr-2" />;
  } else if (tipo === 'audio') {
    return <Mic className="w-4 h-4 mr-2 text-coral" />;
  } else if (tipo === 'video') {
    return <Play className="w-4 h-4 mr-2 text-coral" />;
  } else if (fileType.includes('pdf')) {
    return <FileText className="w-4 h-4 mr-2" />;
  }
  return <File className="w-4 h-4 mr-2" />;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Cache para URLs de anexos
const attachmentUrlCache = new Map<string, { url: string; timestamp: number }>();

const getFileUrl = async (filePath: string): Promise<string> => {
  try {
    // Verificar cache primeiro
    const cached = attachmentUrlCache.get(filePath);
    if (cached && cacheAindaValido(cached.timestamp, Date.now())) {
      console.log('Usando URL em cache para:', filePath);
      return cached.url;
    }

    console.log('Tentando obter URL para arquivo:', filePath);
    
    const { data, error } = await supabase.storage
      .from('chat-attachments')
      .createSignedUrl(filePath, SIGNED_URL_TTL_SECONDS);
    
    if (error) {
      console.error('Erro ao criar URL assinada:', error);
      return '';
    }
    
    const url = data?.signedUrl || '';
    if (url) {
      // Armazenar em cache
      attachmentUrlCache.set(filePath, {
        url,
        timestamp: Date.now()
      });
      
      // Limpar cache antigo periodicamente
      if (attachmentUrlCache.size > 100) {
        const now = Date.now();
        for (const [key, value] of attachmentUrlCache.entries()) {
          if (!cacheAindaValido(value.timestamp, now)) {
            attachmentUrlCache.delete(key);
          }
        }
      }
    }
    
    console.log('URL assinada gerada:', url);
    return url;
  } catch (error) {
    console.error('Erro ao obter URL do arquivo:', error);
    return '';
  }
};

// Modal de vídeo em tela cheia
const VideoPlayerModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  videoUrl: string;
  fileName: string;
}> = ({ isOpen, onClose, videoUrl, fileName }) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);

  React.useEffect(() => {
    if (isOpen && videoRef.current) {
      videoRef.current.play().catch(err => {
        console.log('Não foi possível reproduzir automaticamente:', err);
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-4 right-4 p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-all z-10"
        title="Fechar (ESC)"
      >
        <X className="w-6 h-6 text-white" />
      </button>

      <div
        className="w-full h-full flex items-center justify-center p-8"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full h-full max-w-7xl max-h-full flex flex-col">
          <div className="text-white text-lg font-medium mb-4 px-2">
            {fileName}
          </div>
          <video
            ref={videoRef}
            src={videoUrl}
            controls
            autoPlay
            className="w-full h-full max-h-[calc(100vh-120px)] rounded-lg shadow-2xl"
            style={{ objectFit: 'contain' }}
          >
            Seu navegador não suporta a reprodução de vídeo.
          </video>
        </div>
      </div>
    </div>
  );
};

// Ampliar imagem sem sair do chat: clicar no anexo abre este visualizador, nao
// dispara download. Zoom pela roda do mouse ou pelos botoes, arrastar pra mover
// quando a imagem esta maior que a tela, ESC pra fechar.
const ESCALA_MINIMA = 1;
const ESCALA_MAXIMA = 5;

const limitarEscala = (valor: number) =>
  Math.min(ESCALA_MAXIMA, Math.max(ESCALA_MINIMA, valor));

const ImageViewerModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  imageUrl: string;
  fileName: string;
  onDownload: () => void;
}> = ({ isOpen, onClose, imageUrl, fileName, onDownload }) => {
  const [escala, setEscala] = React.useState(1);
  const [deslocamento, setDeslocamento] = React.useState({ x: 0, y: 0 });
  const [arrastando, setArrastando] = React.useState(false);
  const areaRef = React.useRef<HTMLDivElement>(null);
  const arrasto = React.useRef<{ x: number; y: number; origemX: number; origemY: number } | null>(null);

  const reiniciar = React.useCallback(() => {
    setEscala(1);
    setDeslocamento({ x: 0, y: 0 });
  }, []);

  // Cada abertura comeca no tamanho natural, senao a imagem seguinte herda o
  // zoom da anterior.
  React.useEffect(() => {
    if (isOpen) reiniciar();
  }, [isOpen, reiniciar]);

  const aplicarEscala = React.useCallback((proxima: number) => {
    const limitada = limitarEscala(proxima);
    setEscala(limitada);
    if (limitada === ESCALA_MINIMA) setDeslocamento({ x: 0, y: 0 });
  }, []);

  React.useEffect(() => {
    if (!isOpen) return;

    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === '+' || e.key === '=') {
        aplicarEscala(escala + 0.25);
      } else if (e.key === '-' || e.key === '_') {
        aplicarEscala(escala - 0.25);
      } else if (e.key === '0') {
        reiniciar();
      }
    };

    window.addEventListener('keydown', aoTeclar);
    return () => window.removeEventListener('keydown', aoTeclar);
  }, [isOpen, onClose, escala, aplicarEscala, reiniciar]);

  // React registra onWheel como passivo, entao preventDefault so funciona com
  // listener nativo: sem ele a pagina atras do modal rola junto.
  React.useEffect(() => {
    const area = areaRef.current;
    if (!isOpen || !area) return;

    const aoRolar = (e: WheelEvent) => {
      e.preventDefault();
      aplicarEscala(escala + (e.deltaY < 0 ? 0.25 : -0.25));
    };

    area.addEventListener('wheel', aoRolar, { passive: false });
    return () => area.removeEventListener('wheel', aoRolar);
  }, [isOpen, escala, aplicarEscala]);

  if (!isOpen) return null;

  const podeArrastar = escala > ESCALA_MINIMA;

  const aoPressionar = (e: React.MouseEvent) => {
    if (!podeArrastar) return;
    e.preventDefault();
    arrasto.current = {
      x: e.clientX,
      y: e.clientY,
      origemX: deslocamento.x,
      origemY: deslocamento.y
    };
    setArrastando(true);
  };

  const aoMover = (e: React.MouseEvent) => {
    if (!arrasto.current) return;
    setDeslocamento({
      x: arrasto.current.origemX + (e.clientX - arrasto.current.x),
      y: arrasto.current.origemY + (e.clientY - arrasto.current.y)
    });
  };

  const encerrarArrasto = () => {
    arrasto.current = null;
    setArrastando(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col bg-black bg-opacity-90"
      role="dialog"
      aria-modal="true"
      aria-label={`Visualizar imagem ${fileName}`}
      onClick={onClose}
    >
      <div
        className="flex items-center justify-between gap-2 px-4 py-3 text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-sm md:text-base font-medium truncate" title={fileName}>
          {fileName}
        </span>

        <div className="flex items-center gap-1 shrink-0">
          <span className="text-xs text-white text-opacity-70 w-12 text-right tabular-nums">
            {Math.round(escala * 100)}%
          </span>
          <button
            onClick={() => aplicarEscala(escala - 0.25)}
            disabled={escala <= ESCALA_MINIMA}
            className="p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-all disabled:opacity-40"
            title="Reduzir (-)"
          >
            <ZoomOut className="w-5 h-5" />
          </button>
          <button
            onClick={() => aplicarEscala(escala + 0.25)}
            disabled={escala >= ESCALA_MAXIMA}
            className="p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-all disabled:opacity-40"
            title="Ampliar (+)"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          <button
            onClick={reiniciar}
            className="p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-all"
            title="Tamanho original (0)"
          >
            <RotateCcw className="w-5 h-5" />
          </button>
          <button
            onClick={onDownload}
            className="p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-all"
            title="Baixar imagem"
          >
            <Download className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="p-2 bg-white bg-opacity-10 hover:bg-opacity-20 rounded-full transition-all"
            title="Fechar (ESC)"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={areaRef}
        className="flex-1 overflow-hidden flex items-center justify-center p-4 select-none"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={aoPressionar}
        onMouseMove={aoMover}
        onMouseUp={encerrarArrasto}
        onMouseLeave={encerrarArrasto}
        onDoubleClick={() => aplicarEscala(escala > ESCALA_MINIMA ? ESCALA_MINIMA : 2)}
        style={{ cursor: podeArrastar ? (arrastando ? 'grabbing' : 'grab') : 'zoom-in' }}
      >
        <img
          src={imageUrl}
          alt={fileName}
          draggable={false}
          className="max-w-full max-h-full object-contain rounded shadow-2xl"
          style={{
            transform: `translate(${deslocamento.x}px, ${deslocamento.y}px) scale(${escala})`,
            transition: arrastando ? 'none' : 'transform 120ms ease-out'
          }}
        />
      </div>
    </div>
  );
};

// Exportado para teste isolado: renderizar a mensagem inteira só para conferir
// qual player um mime abre exigiria montar cinco contextos.
export const ChatAttachmentComponent: React.FC<{ attachment: ChatAttachment }> = memo(({ attachment }) => {
  const [url, setUrl] = React.useState<string>('');
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string>('');
  const [isVideoModalOpen, setIsVideoModalOpen] = React.useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = React.useState(false);

  React.useEffect(() => {
    const fetchUrl = async () => {
      setLoading(true);
      setError('');

      console.log('Carregando anexo:', {
        id: attachment.id,
        file_name: attachment.file_name,
        file_path: attachment.file_path,
        file_type: attachment.file_type
      });

      try {
        const fileUrl = await getFileUrl(attachment.file_path);
        if (fileUrl) {
          console.log('URL obtida com sucesso para:', attachment.file_name);
          setUrl(fileUrl);
        } else {
          console.error('URL vazia para arquivo:', attachment.file_path);
          setError('Não foi possível obter URL do arquivo');
        }
      } catch (err) {
        console.error('Erro ao buscar URL do arquivo:', err);
        setError('Erro ao carregar arquivo');
      } finally {
        setLoading(false);
      }
    };

    fetchUrl();
  }, [attachment.file_path]);

  // A classificação mora em attachmentKind: `audio/mp4`, que é o formato que o
  // Safari grava, casaria com um teste ingênuo de container e abriria no player
  // de vídeo como uma tela preta.
  const tipo = tipoDoAnexo(attachment.file_type);
  const isImage = tipo === 'imagem';
  const isVideo = tipo === 'video';
  const isAudio = tipo === 'audio';
  
  const handleDownload = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    try {
      let downloadUrl = url;
      
      if (!downloadUrl && !loading) {
        // Tentar recarregar a URL se não tiver
        console.log('Recarregando URL para download...');
        downloadUrl = await getFileUrl(attachment.file_path);
        if (downloadUrl) {
          setUrl(downloadUrl);
        }
      }
      
      if (downloadUrl) {
        console.log('Iniciando download do arquivo:', attachment.file_name);
        
        // Salvar estado do popup antes do download
        const chatPopupState = localStorage.getItem('chatPopupState');
        console.log('Estado do popup antes do download:', chatPopupState);
        
        // Usar fetch para baixar o arquivo de forma mais controlada
        try {
          const response = await fetch(downloadUrl);
          if (!response.ok) {
            throw new Error('Falha no download');
          }
          
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          
          // Criar link de download
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = attachment.file_name;
          link.style.display = 'none';
          
          // Adicionar ao DOM, fazer download e remover
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Limpar a URL blob
          setTimeout(() => {
            window.URL.revokeObjectURL(blobUrl);
          }, 100);
          
          console.log('Download concluído com sucesso');
          
          // Garantir que o estado do popup seja mantido
          if (chatPopupState) {
            localStorage.setItem('chatPopupState', chatPopupState);
          }
          
        } catch (fetchError) {
          console.warn('Falha no download via fetch, tentando método tradicional:', fetchError);
          
          // Fallback para método tradicional
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = attachment.file_name;
          link.style.display = 'none';
          link.rel = 'noopener noreferrer';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
      } else {
        console.error('URL não disponível para download');
        setError('Erro ao obter URL do arquivo');
      }
    } catch (error) {
      console.error('Erro durante o download:', error);
      setError('Erro durante o download');
    }
  };

  // Fechar modal com ESC
  React.useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isVideoModalOpen) {
        setIsVideoModalOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [isVideoModalOpen]);

  return (
    <>
      <div
        key={attachment.id}
        className={
          isAudio
            ? 'inline-flex items-center gap-1 p-1 rounded bg-white bg-opacity-20 border border-white border-opacity-20'
            : 'flex flex-col p-2 rounded bg-white bg-opacity-20 border border-white border-opacity-20'
        }
      >
      {isImage && url ? (
        <div className="mb-2 relative group">
          <img
            src={url}
            alt={attachment.file_name}
            className="max-w-full max-h-48 rounded object-contain cursor-zoom-in"
            title="Clique para ampliar"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsImageModalOpen(true);
            }}
          />
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsImageModalOpen(true);
            }}
            className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-all opacity-0 group-hover:opacity-100"
            title="Ampliar imagem"
          >
            <Maximize2 className="w-4 h-4 text-white" />
          </button>
        </div>
      ) : null}

      {isAudio && url ? (
        // Largura de mensagem de voz, não da bolha: com `w-full` o player
        // esticava até a borda e o card de um áudio de dez segundos ficava do
        // tamanho de um anexo de documento. A altura corta a faixa que o
        // player nativo reserva sobrando.
        <audio
          src={url}
          controls
          preload="metadata"
          className="w-[240px] max-w-full h-10"
        >
          Seu navegador não suporta a reprodução de áudio.
        </audio>
      ) : null}

      {isVideo && url ? (
        <div className="mb-2 relative group">
          <video
            src={url}
            controls
            className="max-w-full rounded shadow-lg"
            style={{ maxHeight: '300px' }}
          >
            Seu navegador não suporta a reprodução de vídeo.
          </video>
          <button
            onClick={() => setIsVideoModalOpen(true)}
            className="absolute top-2 right-2 p-2 bg-black bg-opacity-50 hover:bg-opacity-70 rounded-full transition-all opacity-0 group-hover:opacity-100"
            title="Abrir em tela cheia"
          >
            <Maximize2 className="w-4 h-4 text-white" />
          </button>
        </div>
      ) : null}
      
      <div className={isAudio ? 'flex items-center' : 'flex items-center justify-between'}>
        {!isAudio && (
          <div className="flex items-center overflow-hidden">
            {getIconForFileType(attachment.file_type)}
            <span className="text-sm truncate max-w-[150px]" title={attachment.file_name}>
              {attachment.file_name}
            </span>
            <span className="text-xs ml-2 text-opacity-70">
              {formatFileSize(attachment.file_size)}
            </span>
          </div>
        )}
        
        <div className="flex items-center gap-1">
          {loading && (
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
          )}
          
          {error && (
            <AlertCircle className="w-4 h-4 text-red-400" title={error} />
          )}
          
          {isImage && (
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsImageModalOpen(true);
              }}
              disabled={loading || !url}
              className="ml-2 p-1 rounded-full hover:bg-white hover:bg-opacity-20 disabled:opacity-50"
              title={loading ? "Carregando..." : "Ver imagem"}
            >
              <Maximize2 className="w-4 h-4" />
            </button>
          )}

          <button
            onClick={handleDownload}
            disabled={loading}
            className={`p-1 rounded-full hover:bg-white hover:bg-opacity-20 disabled:opacity-50 ${isAudio ? '' : 'ml-2'}`}
            title={loading ? "Carregando..." : "Baixar arquivo"}
          >
            <Download className="w-4 h-4" />
          </button>
        </div>
      </div>
      </div>

      {isImage && (
        <ImageViewerModal
          isOpen={isImageModalOpen}
          onClose={() => setIsImageModalOpen(false)}
          imageUrl={url}
          fileName={attachment.file_name}
          onDownload={() => handleDownload()}
        />
      )}

      {isVideo && (
        <VideoPlayerModal
          isOpen={isVideoModalOpen}
          onClose={() => setIsVideoModalOpen(false)}
          videoUrl={url}
          fileName={attachment.file_name}
        />
      )}
    </>
  );
});

// Memoizar o componente principal ChatMessage
const MemoizedChatMessage: React.FC<ChatMessageProps> = memo(({
  message,
  isOwnMessage,
  onReply,
  onJumpToMessage,
  highlighted
}) => {
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const { organization } = useOrganization();
  const { currentRoom, members, editMessage, deleteMessage } = useChat();

  // Nome de cada membro da sala, para o tooltip das reações dizer quem reagiu.
  const nomePorAuthId = useMemo(() => {
    const mapa: Record<string, string> = {};
    for (const m of members) if (m.user?.name) mapa[m.user_auth_id] = m.user.name;
    return mapa;
  }, [members]);
  const nomesMencionaveis = useMemo(() => Object.values(nomePorAuthId), [nomePorAuthId]);
  const toast = useToast();
  const { user } = useAuth();

  const formattedTime = useMemo(
    () => formatMessageTime(message.created_at),
    [message.created_at]
  );

  // Reações ficam em estado local pra contagem mudar no clique, sem esperar o
  // servidor. A lista do servidor manda sempre que a mensagem é recarregada.
  const [reacoes, setReacoes] = useState<ChatMessageReaction[]>(message.reactions || []);

  useEffect(() => {
    setReacoes(message.reactions || []);
  }, [message.reactions]);

  const meuAuthId = user?.id ?? null;

  const aoReagir = useCallback(
    async (novasReacoes: ChatMessageReaction[], emoji: string, emojiAtual: string | null) => {
      if (!meuAuthId) return;

      const anteriores = reacoes;
      setReacoes(novasReacoes);

      try {
        await chatService.setReaction(message.id, meuAuthId, emoji, emojiAtual);
      } catch (err) {
        // Servidor recusou: devolver a contagem ao que era, senão a tela passa
        // a mostrar uma reação que não existe.
        console.error('ChatMessage: Erro ao salvar reação:', err);
        setReacoes(anteriores);
        toast.error('Não foi possível salvar sua reação');
      }
    },
    [meuAuthId, message.id, reacoes, toast]
  );

  const exactTime = useMemo(
    () => formatMessageTimeFull(message.created_at),
    [message.created_at]
  );

  const hasMentions = useMemo(() => {
    return message.mentions && message.mentions.length > 0;
  }, [message.mentions]);

  const hasReplyTo = useMemo(() => {
    return message.parent_id && message.reply_to_message;
  }, [message.parent_id, message.reply_to_message]);

  const projectId = useMemo(() => {
    return currentRoom?.project_id || '';
  }, [currentRoom?.project_id]);

  // Memoizar callbacks
  const handleCreateTask = useCallback(async (taskData: {
    title: string;
    description: string;
    dueDate: string;
    priority: 'low' | 'medium' | 'high';
    assignee: string;
  }) => {
    try {
      if (organization?.id) {
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          toast.error('Usuário não autenticado');
          return;
        }

        await simpleTaskService.createTask({
          title: taskData.title,
          description: taskData.description,
          due_date: taskData.dueDate || null,
          organization_id: organization.id,
          user_id: user.id,
          completed: false
        });
        toast.success('Tarefa criada com sucesso!');
        setIsTaskModalOpen(false);
      } else {
        toast.error('Não foi possível criar a tarefa', 'Organização não encontrada.');
      }
    } catch (error) {
      console.error('Erro ao criar tarefa:', error);
      toast.error('Erro ao criar tarefa');
    }
  }, [organization?.id, toast]);

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setShowContextMenu(true);
  }, []);

  const handleDeleteMessage = useCallback(() => {
    console.log('Mensagem deletada:', message.id);
  }, [message.id]);

  // Editar e excluir a própria mensagem.
  //
  // O que a tela mostra segue messageEditing.ts; quem decide de verdade é o
  // RLS. Se o banco recusar, o serviço lança, a mensagem volta ao que era e o
  // toast conta o que houve — em vez de a tela exibir como salvo o que não foi.
  const apagada = foiApagada(message);
  const editada = foiEditada(message);
  const souODono = podeEditar(message, meuAuthId);

  const [editando, setEditando] = useState(false);
  const [rascunho, setRascunho] = useState(message.content);
  const [salvandoEdicao, setSalvandoEdicao] = useState(false);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);

  // Uma edição de outro dispositivo chega pelo Realtime: o rascunho aberto
  // aqui não pode continuar mostrando o texto antigo depois de fechado.
  useEffect(() => {
    if (!editando) setRascunho(message.content);
  }, [message.content, editando]);

  const abrirEdicao = useCallback(() => {
    setRascunho(message.content);
    setEditando(true);
  }, [message.content]);

  const cancelarEdicao = useCallback(() => {
    setEditando(false);
    setRascunho(message.content);
  }, [message.content]);

  const salvarEdicao = useCallback(async () => {
    if (!edicaoVaiAoBanco(message.content, rascunho)) {
      setEditando(false);
      setRascunho(message.content);
      return;
    }

    setSalvandoEdicao(true);
    try {
      await editMessage(message.id, rascunho);
      setEditando(false);
    } catch (err) {
      console.error('ChatMessage: Erro ao editar mensagem:', err);
      toast.error('Não foi possível editar a mensagem');
    } finally {
      setSalvandoEdicao(false);
    }
  }, [editMessage, message.id, message.content, rascunho, toast]);

  const confirmarExclusao = useCallback(async () => {
    setConfirmandoExclusao(false);
    try {
      await deleteMessage(message.id);
    } catch (err) {
      console.error('ChatMessage: Erro ao excluir mensagem:', err);
      toast.error('Não foi possível excluir a mensagem');
    }
  }, [deleteMessage, message.id, toast]);

  // Enter salva, Shift+Enter quebra linha, Escape desiste — o mesmo acordo do
  // campo de escrever, para não ter que reaprender a teclar dentro da bolha.
  const teclasDaEdicao = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        void salvarEdicao();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelarEdicao();
      }
    },
    [salvarEdicao, cancelarEdicao]
  );

  // Debug logs para verificar dados de reply
  if (message.parent_id) {
    console.log('ChatMessage: Mensagem com parent_id detectada:', {
      messageId: message.id,
      content: message.content,
      parent_id: message.parent_id,
      reply_to: message.reply_to,
      reply_to_message: message.reply_to_message,
      hasReplyTo
    });
  }

  return (
    <>
      <div
        data-message-id={message.id}
        className={`flex group ${isOwnMessage ? 'justify-end' : 'justify-start'} chat-message-row ${
          highlighted ? 'chat-message-row-highlight' : ''
        }`}
      >
        {!isOwnMessage && (
          <div className="w-8 h-8 rounded-full bg-gray-300 mr-2 flex-shrink-0 overflow-hidden">
            {message.user?.avatar_url ? (
              <img 
                src={message.user.avatar_url} 
                alt={message.user.name || 'Avatar'} 
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-400 text-white">
                {message.user?.name ? message.user.name.charAt(0).toUpperCase() : '?'}
              </div>
            )}
          </div>
        )}
        
        <div className={`relative max-w-[70%] ${isOwnMessage ? 'order-1' : 'order-2'}`}>
          {!isOwnMessage && (
            <div className="text-sm font-medium text-gray-700 mb-1 flex items-center">
              {message.user?.name || 'Membro do Projeto'}
              <span className="text-xs text-gray-500 ml-2" title={exactTime}>{formattedTime}</span>
            </div>
          )}
          
          <div
            className={`relative rounded-lg px-3 py-2 shadow-sm ${
              message.is_system_message
                ? 'bg-gray-100 text-gray-600 italic text-sm border border-gray-200'
                : isOwnMessage
                  ? 'chat-message-own'
                  : 'chat-message-other'
            } ${hasMentions ? 'border-l-4 border-l-nk-warn' : ''}`}
            onContextMenu={handleContextMenu}
          >
            {hasReplyTo && (
              <button
                type="button"
                onClick={() => {
                  const originalId = message.reply_to_message?.id ?? message.parent_id;
                  if (originalId) onJumpToMessage?.(originalId);
                }}
                disabled={!onJumpToMessage}
                title="Ir para a mensagem original"
                className={`block w-full text-left mb-2 px-2.5 py-1.5 rounded-md border-l-4 chat-message-reply ${
                  isOwnMessage ? 'chat-message-reply-own' : 'chat-message-reply-other'
                } ${onJumpToMessage ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
              >
                <span className="block text-xs font-semibold mb-1" style={{ color: 'var(--salmon-dark)' }}>
                  {message.reply_to_message?.user?.name || 'Usuário'}
                </span>
                <span className="block text-sm leading-relaxed text-gray-600">
                  {/* A citação não pode continuar exibindo o texto de uma
                      mensagem que a pessoa mandou apagar. */}
                  {foiApagada(message.reply_to_message ?? {})
                    ? <span className="italic opacity-70">{TEXTO_DE_MENSAGEM_APAGADA}</span>
                    : message.reply_to_message?.content}
                </span>
              </button>
            )}

            {apagada ? (
              <div className="italic opacity-70 flex items-center gap-1.5">
                <Trash2 className="w-3.5 h-3.5 flex-shrink-0" />
                {TEXTO_DE_MENSAGEM_APAGADA}
              </div>
            ) : editando ? (
              <div className="flex flex-col gap-2 min-w-[220px]">
                <textarea
                  value={rascunho}
                  onChange={e => setRascunho(e.target.value)}
                  onKeyDown={teclasDaEdicao}
                  autoFocus
                  rows={Math.min(6, rascunho.split('\n').length || 1)}
                  aria-label="Editar mensagem"
                  className="w-full resize-none rounded-md border border-gray-300 p-2 text-gray-800 bg-white focus:border-salmon focus:ring-1 focus:ring-salmon"
                />
                <div className="flex items-center gap-2 justify-end">
                  <button
                    type="button"
                    onClick={cancelarEdicao}
                    disabled={salvandoEdicao}
                    className="px-2 py-1 text-xs rounded bg-white bg-opacity-70 text-gray-700 hover:bg-opacity-100 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    onClick={() => { void salvarEdicao(); }}
                    disabled={salvandoEdicao}
                    className="px-2 py-1 text-xs rounded bg-coral-deep text-white hover:bg-coral-press disabled:opacity-50 flex items-center gap-1"
                  >
                    <Check className="w-3 h-3" />
                    {salvandoEdicao ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </div>
            ) : (
              message.content && (
                <div className="whitespace-pre-wrap break-words">
                  {formatMessageWithMentions(message.content, nomesMencionaveis)}
                  {editada && (
                    <span className="text-xs opacity-60 ml-1.5 align-baseline" title="Mensagem editada">
                      (editada)
                    </span>
                  )}
                </div>
              )
            )}

            {!apagada && message.attachments && message.attachments.length > 0 && (
              <div className="mt-2 space-y-2">
                {message.attachments.map(attachment => (
                  <ChatAttachmentComponent key={attachment.id} attachment={attachment} />
                ))}
              </div>
            )}

            {isOwnMessage && (
              <div className="text-xs mt-1 text-right flex items-center justify-end chat-message-time" title={exactTime}>
                {formattedTime}
              </div>
            )}

            {/* Barra de ações presa à bolha, e não ao bloco com as pílulas de
                reação: ancorada embaixo do bloco, ela descia junto quando a
                mensagem tinha reação e ficava longe do texto. As pílulas ficam
                à esquerda, a barra à direita. */}
            {!message.is_system_message && !apagada && (
              <div className="absolute top-full -mt-2 right-1 z-10 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <ReactionButton reactions={reacoes} meuAuthId={meuAuthId} onChange={aoReagir} />
                {souODono && !editando && (
                  <button
                    onClick={abrirEdicao}
                    className="text-gray-400 hover:text-gray-600 bg-white rounded-full p-0.5 shadow-sm hover:shadow"
                    title="Editar mensagem"
                    aria-label="Editar mensagem"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </button>
                )}
                {podeExcluir(message, meuAuthId) && (
                  <button
                    onClick={() => setConfirmandoExclusao(true)}
                    className="text-gray-400 hover:text-nk-error bg-white rounded-full p-0.5 shadow-sm hover:shadow"
                    title="Excluir mensagem"
                    aria-label="Excluir mensagem"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                )}
                {onReply && (
                  <button 
                    onClick={onReply}
                    className="text-gray-400 hover:text-gray-600 bg-white rounded-full p-0.5 shadow-sm hover:shadow"
                    title="Responder"
                  >
                    <Reply className="w-3.5 h-3.5" />
                  </button>
                )}
                {/* A tarefa nasce no projeto da sala; conversa privada não tem projeto. */}
                {projectId && (
                  <button
                    onClick={() => setIsTaskModalOpen(true)}
                    className="text-gray-400 hover:text-gray-600 bg-white rounded-full p-0.5 shadow-sm hover:shadow"
                    title="Criar Tarefa"
                  >
                    <CheckSquare className="w-3.5 h-3.5" />
                  </button>
                )}
                <button 
                  onClick={handleContextMenu}
                  className="text-gray-400 hover:text-gray-600 bg-white rounded-full p-0.5 shadow-sm hover:shadow"
                  title="Mais opções"
                >
                  <MoreVertical className="w-3.5 h-3.5" />
                </button>
              </div>
            )}
          </div>

          {/* Mensagem apagada fica sem reações: as pílulas continuariam
              contando o que as pessoas acharam de um texto que não está mais
              lá. As linhas seguem no banco — o autor não tem permissão para
              apagar a reação de outra pessoa. */}
          {!message.is_system_message && !apagada && (
            <ReactionPills
              reactions={reacoes}
              meuAuthId={meuAuthId}
              nomePorAuthId={nomePorAuthId}
              onChange={aoReagir}
            />
          )}
        </div>
      </div>

      {isTaskModalOpen && (
        <MessageToTaskModal
          isOpen={isTaskModalOpen}
          onClose={() => setIsTaskModalOpen(false)}
          message={{
            id: message.id,
            content: message.content,
            sender: message.user?.name || 'Membro do Projeto',
            timestamp: message.created_at || new Date().toISOString(),
            type: 'text',
            projectId: projectId
          }}
          onCreateTask={handleCreateTask}
        />
      )}

      {/* Confirmação em modal, e não em `window.confirm`: o diálogo nativo
          trava a aba inteira e some do desenho do produto. A exclusão não tem
          volta — o texto sai da linha e os anexos saem do Storage. */}
      {confirmandoExclusao && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          aria-label="Excluir mensagem"
        >
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-lg font-semibold mb-2">Excluir mensagem</h3>
            <p className="text-gray-600 mb-6 text-sm">
              A mensagem sai da conversa para todo mundo e não pode ser recuperada.
              {message.attachments && message.attachments.length > 0
                ? ' O arquivo anexado também será removido.'
                : ''}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => setConfirmandoExclusao(false)}
                className="btn-secondary flex-1"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => { void confirmarExclusao(); }}
                className="btn-danger flex-1"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      <MessageContextMenu
        messageId={message.id}
        messageContent={message.content}
        messageSenderId={message.user_auth_id}
        isOwnMessage={isOwnMessage}
        isVisible={showContextMenu}
        position={contextMenuPosition}
        onClose={() => setShowContextMenu(false)}
        onReply={onReply}
        onDelete={handleDeleteMessage}
        onEdit={souODono ? abrirEdicao : undefined}
        onRequestDelete={
          podeExcluir(message, meuAuthId) ? () => setConfirmandoExclusao(true) : undefined
        }
      />
    </>
  );
});

// Renderiza o texto da mensagem: links viram <a> clicável, menções ganham
// destaque, o resto é texto. A quebra em partes fica em messageTokens.ts, que é
// testada isoladamente.
const formatMessageWithMentions = (content: string, nomesMencionaveis: string[]): JSX.Element => {
  const tokens = splitMessageIntoTokens(content, nomesMencionaveis);

  if (tokens.length === 0) return <>{content}</>;

  return (
    <>
      {tokens.map((token, index) => {
        if (token.type === 'mention') {
          return (
            <span key={index} className="chat-message-mention">
              {token.value}
            </span>
          );
        }

        if (token.type === 'link') {
          return (
            <a
              key={index}
              href={token.href}
              target="_blank"
              // noopener/noreferrer: sem isso a página aberta ganha acesso à
              // nossa via window.opener, e o link vem de quem escreveu a mensagem.
              rel="noopener noreferrer"
              className="chat-message-link"
              onClick={e => e.stopPropagation()}
            >
              {token.value}
            </a>
          );
        }

        return <span key={index}>{token.value}</span>;
      })}
    </>
  );
};

export default MemoizedChatMessage;
